import {
  Avatar,
  Box,
  Button,
  Divider,
  Fade,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  SkeletonCircle,
  SkeletonText,
  Spacer,
  Text,
  useClipboard,
  useDisclosure,
} from '@chakra-ui/react'
import moment from 'moment'
import React, {
  lazy,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { AiOutlineComment } from 'react-icons/ai'
import { BsFilter, BsThreeDotsVertical } from 'react-icons/bs'
import { FiFilter } from 'react-icons/fi'
import { GoCheckCircle } from 'react-icons/go'
import { ImLink } from 'react-icons/im'
import { PiPushPinLight } from 'react-icons/pi'
import { RiDeleteBin2Line } from 'react-icons/ri'
import { useHistory, useLocation } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import { PermissionsEnum, checkOperationPermission } from '../../../../../utils'
import {
  ACLPermissionEnum,
  ThreadFilter,
  ThreadSort,
} from '../../../../constants'
import useToast from '../../../../hooks/useToast'
import type {
  FileDetailsResponse,
  ResolveThreadResponse,
  ThreadErrorResponse,
  Threads,
} from '../../../../interfaces'
import { MLContext, UserContext } from '../../../../providers'
import { MediaLibraryActions } from '../../../../providers/MLProvider'
import PermissionGuard from '../../../PermissionGuard'
import DeleteThreadModal from './DeleteThreadModal'
import VersionContainer from './VersionContainer'

const Editor = lazy(() => import('../../../TextEditor/Editor'))

interface Props {
  fileDetails: FileDetailsResponse
}

const ThreadList = ({ fileDetails }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const [
    {
      threads = [],
      selectedThread,
      threadFilter,
      threadSort,
      showNewThreadPanel,
      threadListUiLoaded,
    },
    dispatch,
  ] = useContext(MLContext)
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const toast = useToast()
  const location = useLocation()
  const history = useHistory()

  const [hoveredThreadId, setHoveredThreadId] = useState<string>('')
  const [threadIdToBeDeleted, setThreadIdToBeDeleted] = useState<string>('')

  const {
    isOpen: isOpenDeleteThreadModal,
    onOpen: onOpenDeleteThreadModal,
    onClose: onCloseDeleteThreadModal,
  } = useDisclosure()

  const focusedThreadRef = useRef<HTMLDivElement | null>(null)
  const { onCopy } = useClipboard(
    `${
      window.location.origin + window.location.pathname
    }?focusedThreadId=${hoveredThreadId}`,
  )

  const searchParams = new URLSearchParams(location.search)
  const focusedThreadIdParam = searchParams.get('focusedThreadId')

  const scrollToThread = (): void => {
    if (focusedThreadRef.current) {
      focusedThreadRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      })
    }
  }

  const getThreads = async (): Promise<void> => {
    const res = (await request.get(
      `/api/media-library/${user?.clientNumber}/files/${fileDetails.fileId}/threads`,
    )) as Threads[]

    if (request.error) {
      toast({
        description: t('error_fetching_comments', 'Error in fetching comments'),
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
        variant: 'outline',
      })
    } else {
      dispatch({
        type: MediaLibraryActions.SET_THREADS,
        payload: res,
      })

      if (focusedThreadIdParam) {
        dispatch({
          type: MediaLibraryActions.SET_THREAD_FILTER,
          payload: ThreadFilter.ALL,
        })
        dispatch({
          type: MediaLibraryActions.SET_SELECTED_THREAD,
          payload: res.find(th => th.threadId === focusedThreadIdParam) ?? null,
        })
        scrollToThread()
      }
    }
  }

  const setThreadResolution = async (
    thread: Threads,
    resolve: boolean,
  ): Promise<void> => {
    dispatch({
      type: MediaLibraryActions.SET_SELECTED_THREAD,
      payload: null,
    })

    const res = (await request.patch(
      // eslint-disable-next-line max-len
      `/api/media-library/${user?.clientNumber}/files/${fileDetails.fileId}/threads/${thread.threadId}`,
      {
        resolve,
      },
    )) as ResolveThreadResponse | ThreadErrorResponse

    if (request.error) {
      toast({
        description:
          (res as ThreadErrorResponse).message ??
          t('error_resolving_thread', 'Error in resolving thread'),
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
        variant: 'outline',
      })
    }

    void getThreads()
  }

  useEffect(() => {
    void getThreads()

    return () => {
      dispatch({
        type: MediaLibraryActions.SET_SELECTED_THREAD,
        payload: null,
      })
      dispatch({
        type: MediaLibraryActions.SET_THREADS,
        payload: [],
      })
    }
  }, [])

  useEffect(() => {
    if (selectedThread) {
      history.push({
        pathname: location.pathname,
        search: new URLSearchParams({
          focusedThreadId: selectedThread.threadId,
        }).toString(),
      })
    } else {
      const queryParams = new URLSearchParams(location.search)
      queryParams.delete('focusedThreadId')

      history.push({
        pathname: location.pathname,
        search: queryParams.toString(),
      })
    }
  }, [selectedThread])

  const threadList = useMemo(() => {
    const filterFunction = (thread: Threads): boolean => {
      switch (threadFilter) {
        case ThreadFilter.UNRESOLVED:
          return !thread.resolved
        default:
          return true
      }
    }

    const sortCriteria = {
      [ThreadSort.DESC_UPDATED]: (a: Threads, b: Threads) =>
        a.updatedAt > b.updatedAt ? -1 : a.updatedAt < b.updatedAt ? 1 : 0,
      [ThreadSort.DESC_CREATED]: (a: Threads, b: Threads) =>
        a.createdAt > b.createdAt ? -1 : a.createdAt < b.createdAt ? 1 : 0,
    }

    return threads.filter(filterFunction).sort(sortCriteria[threadSort])
  }, [threadSort, threadFilter, threads])

  return (
    <>
      <Flex direction="column" position="relative" width="full">
        <Flex direction="row" p="8px">
          <PermissionGuard
            permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
          >
            {(hasPermission: boolean) =>
              hasPermission &&
              checkOperationPermission({
                user,
                aclPermission: [
                  ACLPermissionEnum.CONTRIBUTE,
                  ACLPermissionEnum.MANAGE,
                ],
                selectedFiles: [fileDetails],
                selectedFolders: [],
                selectedMediaCollections: [],
              }) ? (
                <Button
                  variant="link"
                  color="#2D3748"
                  fontWeight={400}
                  cursor="pointer"
                  leftIcon={<AiOutlineComment size="18px" color="#2D3748" />}
                  borderRadius="4px"
                  padding="4px 8px"
                  _hover={{
                    background: 'appPaleGreyTwo',
                  }}
                  background={
                    showNewThreadPanel ? 'appPaleGreyThree' : 'transparent'
                  }
                  isLoading={!threadListUiLoaded}
                  isDisabled={!threadListUiLoaded}
                  loadingText={t('add_comment', 'Add comment')}
                  onClick={(e) => {
                    dispatch({
                      type: MediaLibraryActions.SET_SELECTED_THREAD,
                      payload: null,
                    })
                    dispatch({
                      type: MediaLibraryActions.SHOW_NEW_THREAD_PANEL,
                      payload: !showNewThreadPanel,
                    })
                  }}
                >
                  {t('add_comment', 'Add comment')}
                </Button>
              ) : (
                <></>
              )
            }
          </PermissionGuard>
          <Spacer />
          <Menu isLazy>
            <MenuButton
              aria-label={t('sort_threads', 'Sort threads')}
              as={IconButton}
              variant="link"
              color="#2D3748"
              icon={<BsFilter size="20px" />}
              minWidth="32px"
              _hover={{
                background: 'appPaleGreyTwo',
              }}
            />
            <MenuList boxShadow="0 3px 6px 0 rgba(0, 0, 0, 0.1)">
              <MenuOptionGroup
                defaultValue={ThreadSort.DESC_UPDATED}
                value={threadSort}
                type="radio"
              >
                <MenuItemOption
                  value={ThreadSort.DESC_UPDATED}
                  onClick={() => {
                    dispatch({
                      type: MediaLibraryActions.SET_THREAD_SORT,
                      payload: ThreadSort.DESC_UPDATED,
                    })
                  }}
                >
                  {t('sort_by_recent_activity', 'Sort by recent activity')}
                </MenuItemOption>
                <MenuItemOption
                  value={ThreadSort.DESC_CREATED}
                  onClick={() => {
                    dispatch({
                      type: MediaLibraryActions.SET_THREAD_SORT,
                      payload: ThreadSort.DESC_CREATED,
                    })
                  }}
                >
                  {t('sort_by_recent_threads', 'Sort by recent threads')}
                </MenuItemOption>
              </MenuOptionGroup>
            </MenuList>
          </Menu>
          <Menu isLazy>
            <MenuButton
              aria-label={t('filter_threads', 'Filter threads')}
              variant="link"
              as={IconButton}
              color="#2D3748"
              icon={<FiFilter width="16px" />}
              minWidth="32px"
              _hover={{
                background: 'appPaleGreyTwo',
              }}
            />
            <MenuList boxShadow="0 3px 6px 0 rgba(0, 0, 0, 0.1)">
              <MenuOptionGroup
                defaultValue={ThreadFilter.UNRESOLVED}
                value={threadFilter}
                type="radio"
              >
                <MenuItemOption
                  value={ThreadFilter.UNRESOLVED}
                  onClick={() => {
                    dispatch({
                      type: MediaLibraryActions.SET_THREAD_FILTER,
                      payload: ThreadFilter.UNRESOLVED,
                    })
                  }}
                >
                  {t('unresolved_threads_only', 'Unresolved threads only')}
                </MenuItemOption>
                <MenuItemOption
                  value={ThreadFilter.ALL}
                  onClick={() => {
                    dispatch({
                      type: MediaLibraryActions.SET_THREAD_FILTER,
                      payload: ThreadFilter.ALL,
                    })
                  }}
                >
                  {t('all_threads', 'All threads')}
                </MenuItemOption>
              </MenuOptionGroup>
            </MenuList>
          </Menu>
        </Flex>
        <Divider color="appPaleLilac" />
        {threadList.length === 0 ? (
          <Flex direction="row" p="16px">
            <Icon
              aria-label={t(
                'click_to_comment',
                'Click on the picture to comment',
              )}
              boxSize="16px"
              color="appSlate"
              mt="4px"
              as={PiPushPinLight}
            />
            <Text ml="8px" color="appSlate">
              {fileDetails.fileType === 'image'
                ? t(
                    'thread_info_picture',
                    // eslint-disable-next-line max-len
                    'Give feedback, ask a question, or just leave a note. Click anywhere on the picture to leave a comment.',
                  )
                : t(
                    'thread_info',
                    // eslint-disable-next-line max-len
                    'Give feedback, ask a question, or just leave a note.',
                  )}
            </Text>
          </Flex>
        ) : (
          <Box overflowY="scroll">
            {threadList.map((thread, i) => (
              <Suspense
                key={thread.threadId}
                fallback={
                  <Box padding="24px">
                    <SkeletonCircle />
                    <SkeletonText
                      mt="4"
                      noOfLines={4}
                      spacing="4"
                      skeletonHeight="2"
                    />
                  </Box>
                }
              >
                <Box
                  key={thread.threadId}
                  ref={
                    focusedThreadIdParam === thread.threadId
                      ? focusedThreadRef
                      : null
                  }
                  onClick={() => {
                    if (thread.threadId === selectedThread?.threadId) {
                      dispatch({
                        type: MediaLibraryActions.SET_SELECTED_THREAD,
                        payload: null,
                      })
                    } else {
                      dispatch({
                        type: MediaLibraryActions.SET_SELECTED_THREAD,
                        payload: thread,
                      })
                    }
                  }}
                  onMouseEnter={() => {
                    setHoveredThreadId(thread.threadId)
                  }}
                  background={
                    selectedThread?.threadId === thread.threadId
                      ? 'appPaleGreyThree'
                      : ''
                  }
                  onMouseLeave={() => {
                    setHoveredThreadId('')
                  }}
                  _hover={{
                    background:
                      selectedThread?.threadId !== thread.threadId
                        ? 'appPaleGreyTwo'
                        : '',
                  }}
                >
                  <Flex p="16px" direction="column">
                    {(thread.versionId &&
                      thread.versionId !== fileDetails.versionInfo.id) ||
                    (!thread.versionId &&
                      thread.fileId !== fileDetails.versionInfo.id) ? (
                      <Box mb="16px">
                        <VersionContainer
                          versionId={thread.versionId ?? fileDetails.fileId}
                          fileId={thread.fileId}
                        />
                      </Box>
                    ) : null}
                    <Flex direction="row" mb="8px" maxWidth="18vw">
                      <Avatar
                        name={thread.comments[0].userName}
                        h="40px"
                        w="40px"
                      />
                      <Flex
                        direction="column"
                        ml="8px"
                        flex="1"
                        overflow="hidden"
                      >
                        <Text fontWeight="600" isTruncated>
                          {thread.comments[0].userName}
                        </Text>
                        <Text fontSize="12px" color="#4A5568" isTruncated>
                          {moment(thread.comments[0].createdAt).fromNow()}
                        </Text>
                      </Flex>
                      <Flex direction="row" alignSelf="flex-end" align="center">
                        <Fade
                          in={
                            hoveredThreadId === thread.threadId ||
                            selectedThread?.threadId === thread.threadId
                          }
                          unmountOnExit={true}
                        >
                          <IconButton
                            aria-label={t(
                              'copy_link_to_thread',
                              'Copy link to thread',
                            )}
                            icon={<ImLink color="#2D3748" size="12px" />}
                            variant="link"
                            minWidth="32px"
                            minHeight="32px"
                            onClick={event => {
                              event.stopPropagation()
                              onCopy()
                              toast({
                                description: t(
                                  'copied_to_clipboard',
                                  'Copied to clipboard',
                                ),
                                status: 'success',
                                isClosable: true,
                                duration: 5000,
                                position: 'top-right',
                                variant: 'outline',
                              })
                            }}
                          />
                        </Fade>
                        <PermissionGuard
                          permission={
                            PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE
                          }
                        >
                          {(hasPermission: boolean) =>
                            hasPermission &&
                            checkOperationPermission({
                              user,
                              aclPermission: [ACLPermissionEnum.MANAGE],
                              selectedFiles: [fileDetails],
                              selectedFolders: [],
                              selectedMediaCollections: [],
                            }) ? (
                              <Fade
                                in={
                                  hoveredThreadId === thread.threadId ||
                                  selectedThread?.threadId === thread.threadId
                                }
                                unmountOnExit={true}
                              >
                                <Menu isLazy>
                                  <MenuButton
                                    as={IconButton}
                                    aria-label={t(
                                      'more_options',
                                      'More options',
                                    )}
                                    icon={
                                      <BsThreeDotsVertical
                                        color="#2D3748"
                                        size="14px"
                                      />
                                    }
                                    onClick={event => {
                                      event.stopPropagation()
                                    }}
                                    variant="link"
                                    minWidth="32px"
                                    minHeight="32px"
                                  />
                                  <MenuList>
                                    <MenuItem
                                      icon={<RiDeleteBin2Line size="16px" />}
                                      color="appBrick"
                                      onClick={e => {
                                        e.stopPropagation()
                                        setThreadIdToBeDeleted(thread.threadId)
                                        onOpenDeleteThreadModal()
                                      }}
                                    >
                                      {t('delete', 'Delete')}
                                    </MenuItem>
                                  </MenuList>
                                </Menu>
                              </Fade>
                            ) : (
                              <></>
                            )
                          }
                        </PermissionGuard>
                        <Fade
                          in={
                            hoveredThreadId === thread.threadId ||
                            thread.resolved ||
                            selectedThread?.threadId === thread.threadId
                          }
                          unmountOnExit={true}
                        >
                          <IconButton
                            aria-label={t('resolve_thread', 'Resolve thread')}
                            icon={
                              <GoCheckCircle
                                color={thread.resolved ? '#20AC78' : '#2D3748'}
                                size="16px"
                              />
                            }
                            variant="link"
                            minWidth="32px"
                            minHeight="32px"
                            cursor={
                              !checkOperationPermission({
                                user,
                                aclPermission: [
                                  ACLPermissionEnum.CONTRIBUTE,
                                  ACLPermissionEnum.MANAGE,
                                ],
                                selectedFiles: [fileDetails],
                                selectedFolders: [],
                                selectedMediaCollections: [],
                              }) ||
                              !user?.permissions.includes(
                                PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                              )
                                ? 'default'
                                : 'pointer'
                            }
                            onClick={async event => {
                              event.stopPropagation()
                              if (
                                !checkOperationPermission({
                                  user,
                                  aclPermission: [
                                    ACLPermissionEnum.CONTRIBUTE,
                                    ACLPermissionEnum.MANAGE,
                                  ],
                                  selectedFiles: [fileDetails],
                                  selectedFolders: [],
                                  selectedMediaCollections: [],
                                }) ||
                                !user?.permissions.includes(
                                  PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                                )
                              ) {
                                return
                              }
                              void setThreadResolution(thread, !thread.resolved)
                            }}
                          />
                        </Fade>
                      </Flex>
                    </Flex>
                    <Flex justify="left">
                      {typeof thread.timestamp === 'number' ? (
                        <Button
                          as="a"
                          variant="link"
                          cursor="pointer"
                          fontSize="12px"
                          color="appClearBlue"
                          minWidth={0}
                          mb="7px"
                          onClick={e => {
                            dispatch({
                              type: MediaLibraryActions.SET_VIDEO_TIME,
                              payload: thread.timestamp!,
                            })
                          }}
                        >
                          {moment.utc(1000 * thread.timestamp).format('mm:ss')}
                        </Button>
                      ) : null}
                    </Flex>
                    <Editor
                      isNewThread={true}
                      editable={false}
                      initialEditorState={thread.comments[0].commentBody}
                    />
                    {thread.comments.length > 1 ? (
                      <Box alignSelf="start">
                        <Button
                          as="a"
                          variant="link"
                          fontWeight="600"
                          color="appClearBlue"
                          mt="16px"
                        >
                          {thread.comments.length === 2
                            ? t('one_reply', '1 Reply')
                            : `${thread.comments.length - 1} Replies`}
                        </Button>
                      </Box>
                    ) : null}
                  </Flex>
                  {i !== threadList.length - 1 ? (
                    <Divider color="appPaleLilac" />
                  ) : null}
                </Box>
              </Suspense>
            ))}
          </Box>
        )}
      </Flex>
      <DeleteThreadModal
        isOpen={isOpenDeleteThreadModal}
        fileId={fileDetails.fileId}
        threadId={threadIdToBeDeleted}
        onClose={onCloseDeleteThreadModal}
        getThreads={getThreads}
      />
    </>
  )
}

export default ThreadList
